/**
 * TanStack Query hooks against the message-gateway `/knowledge` REST surface.
 *
 * Two read endpoints:
 *  - `useSearchKnowledge` — returns preview rows (id/title/snippet/score)
 *    for the dialog's result list. Keyed by the full args tuple so changing
 *    a filter naturally swaps caches without manual invalidation. Empty `q`
 *    short-circuits the request — the backend would return an empty list
 *    anyway and we'd rather keep network noise out of the dialog open.
 *  - `useKnowledgeEntry` — returns the full body for the detail pane.
 *    Disabled when no id is selected so the hook can be called
 *    unconditionally from a single render path (matches the dialog flow:
 *    "open with no selection → user clicks a row → fetch detail").
 *
 * Cache strategy:
 *  - The result *list* is short-lived (``SEARCH_STALE_MS``). Other users
 *    can promote analytical variables into the same scope the caller just searched, so the
 *    list can shift between visits and we revalidate aggressively. We use
 *    ``placeholderData: keepPreviousData`` so the previous match list
 *    stays visible during a refetch instead of flashing the loading
 *    spinner — that's what makes the typing experience feel instant.
 *  - The individual *entry body* is long-lived (``ENTRY_STALE_MS``).
 *    Entries change less often than the aggregate list (mostly only
 *    when someone re-finalizes or promotes), and the promote mutation
 *    already invalidates the affected ids on success.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { authFetch } from './authFetch';
import { knowledgeKeys } from './knowledgeKeys';
import {
  AvPromoteResultEnvelopeSchema,
  KnowledgeEntryEnvelopeSchema,
  KnowledgeFilterOptionsSchema,
  KnowledgeSearchListEnvelopeSchema,
  type AvPromoteResult,
  type AvPromotionScope,
  type KnowledgeEntry,
  type KnowledgeFilterOptions,
  type KnowledgeSearchArgs,
  type KnowledgeSearchResult,
} from './knowledgeTypes';

const buildSearchPath = (args: KnowledgeSearchArgs): string => {
  const params = new URLSearchParams();
  params.set('q', args.q);
  if (args.kinds) {
    for (const k of args.kinds) {
      params.append('kind', k);
    }
  }
  if (args.activeDataset) {
    params.set('activeDataset', args.activeDataset);
  }
  if (args.sessionId) {
    params.set('sessionId', args.sessionId);
  }
  if (args.limit !== undefined) {
    params.set('limit', String(args.limit));
  }
  if (args.minScore !== undefined) {
    params.set('minScore', String(args.minScore));
  }
  return `/knowledge/search?${params.toString()}`;
};

/**
 * Stale window for the search list. 30s is short enough that another
 * user's promotion shows up on the next visit (even without a window
 * focus event); long enough that quickly tabbing through filter chips
 * during the same session reads from cache instead of the network.
 */
const SEARCH_STALE_MS = 30_000;
/**
 * Stale window for individual entries. 5 minutes — entries are stable
 * outside an explicit promote, and the mutation invalidates them on
 * success, so a longer cache here is safe and cuts latency on the
 * "back to a recent entry" navigation.
 */
const ENTRY_STALE_MS = 5 * 60_000;

export const useSearchKnowledge = (args: KnowledgeSearchArgs, options?: { enabled?: boolean }) => {
  const trimmedQuery = args.q.trim();
  return useQuery<KnowledgeSearchResult[]>({
    queryKey: knowledgeKeys.search(args),
    queryFn: async () => {
      const raw = await authFetch<unknown>(buildSearchPath({ ...args, q: trimmedQuery }));
      const parsed = KnowledgeSearchListEnvelopeSchema.parse(raw);
      return parsed.data ?? [];
    },
    enabled: options?.enabled !== false && trimmedQuery.length > 0,
    staleTime: SEARCH_STALE_MS,
    // Keep the previous result list visible while refetching for a new
    // query / filter set, so the list doesn't flash empty between
    // keystrokes and the user has visible state to scan.
    placeholderData: keepPreviousData,
  });
};

export const useKnowledgeEntry = (entryId: string | null) => {
  return useQuery<KnowledgeEntry>({
    queryKey: knowledgeKeys.entry(entryId ?? ''),
    queryFn: async () => {
      const raw = await authFetch<unknown>(`/knowledge/${encodeURIComponent(entryId ?? '')}`);
      return KnowledgeEntryEnvelopeSchema.parse(raw).data;
    },
    enabled: !!entryId,
    staleTime: ENTRY_STALE_MS,
  });
};

export const useKnowledgeFilterOptions = (options?: { enabled?: boolean }) => {
  return useQuery<KnowledgeFilterOptions>({
    queryKey: knowledgeKeys.filterOptions(),
    queryFn: async () => {
      const raw = await authFetch<unknown>('/knowledge/scopes');
      return KnowledgeFilterOptionsSchema.parse(raw);
    },
    enabled: options?.enabled !== false,
    staleTime: Infinity,
  });
};

export interface PromoteAvVariables {
  slug: string;
  targetScope: AvPromotionScope;
}

/**
 * Mutation against ``POST /knowledge/avs/{slug}/promote``.
 *
 * The user / org the analytical variable moves into come from the verified Auth0 token
 * server-side — never from the request body — so the only client input
 * is the slug and target_scope. Caller surfaces the success result so a
 * Sharing control can update the chip in place; failures bubble as
 * ``AuthFetchError`` (the 409/403/404 status surfaces on
 * ``error.status``) so the UI can render the directive message verbatim.
 *
 * Successful promotion invalidates any cached entry detail for the slug
 * so the next read of ``av.<slug>`` picks up the new scope.
 */
export const usePromoteAv = () => {
  const queryClient = useQueryClient();
  return useMutation<AvPromoteResult, Error, PromoteAvVariables>({
    mutationFn: async ({ slug, targetScope }) => {
      const raw = await authFetch<unknown>(`/knowledge/avs/${encodeURIComponent(slug)}/promote`, {
        method: 'POST',
        body: JSON.stringify({ targetScope }),
      });
      return AvPromoteResultEnvelopeSchema.parse(raw).data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: knowledgeKeys.entry(`av.${data.slug}`) });
      if (data.frozenVersionId) {
        queryClient.invalidateQueries({ queryKey: knowledgeKeys.entry(data.frozenVersionId) });
      }
    },
  });
};
